import { useEffect, useState } from 'react';

import { useLocation, useParams } from 'react-router-dom';

import useSections from 'api/useSections';
import useTabs from 'api/useTabs';
import useTheme from 'api/useTheme';
import { ESectionOfferType } from 'constants/enums';
import { Base64 } from 'js-base64';

import { defaultStoreTheme } from './defaults';
import { SectionsItem, StoreThemeFormValues } from './storeTheme.types';

export const useGetStoreInitialValues = () => {
  const location = useLocation();

  const { publisherId } = useParams();

  const [initialValues, setInitialValue] =
    useState<StoreThemeFormValues>(defaultStoreTheme);
  const { getStoreScreen: storeScreenTheme } = useTheme(publisherId);
  const { getSections } = useSections(publisherId);
  const { getTabs } = useTabs(publisherId);

  const safeInitialValues = initialValues || defaultStoreTheme;

  useEffect(() => {
    setInitialValue({
      defaultBanner:
        storeScreenTheme?.data?.defaultBanner ||
        defaultStoreTheme.defaultBanner,
      bundleSections:
        getSections.data?.result.filter(
          (section: SectionsItem) =>
            !section.offerType || section.offerType === ESectionOfferType.BUNDLE
        ) || defaultStoreTheme.bundleSections,
      promotionSections:
        getSections.data?.result.filter(
          (section: SectionsItem) =>
            section.offerType === ESectionOfferType.PROMOTION
        ) || defaultStoreTheme.promotionSections,
      tabs: getTabs.data || defaultStoreTheme.tabs,
      buttonColor: {
        colorOne:
          storeScreenTheme.data?.buttonColor?.colorOne ||
          defaultStoreTheme.buttonColor.colorOne,
        colorTwo:
          storeScreenTheme.data?.buttonColor?.colorTwo ||
          defaultStoreTheme.buttonColor.colorTwo,
        gradientDirection:
          storeScreenTheme.data?.buttonColor?.gradientDirection ||
          defaultStoreTheme.buttonColor.gradientDirection
      },
      buttonTextColor:
        storeScreenTheme.data?.buttonTextColor ||
        defaultStoreTheme.buttonTextColor,
      header: {
        backgroundOpacity:
          storeScreenTheme?.data?.header?.backgroundOpacity ??
          defaultStoreTheme.header.backgroundOpacity,
        showLogo:
          storeScreenTheme?.data?.header?.showLogo ??
          defaultStoreTheme.header.showLogo,
        showPlayerName:
          storeScreenTheme?.data?.header?.showPlayerName ??
          defaultStoreTheme.header.showPlayerName,
        isSticky:
          storeScreenTheme?.data?.header?.isSticky ??
          defaultStoreTheme.header.isSticky
      },
      backToGameButton: {
        active:
          storeScreenTheme?.data?.backToGameButton?.active ||
          defaultStoreTheme.backToGameButton.active,
        buttonImage:
          storeScreenTheme?.data?.backToGameButton?.buttonImage ||
          defaultStoreTheme.backToGameButton.buttonImage,
        position:
          storeScreenTheme?.data?.backToGameButton?.position ||
          defaultStoreTheme.backToGameButton.position
      },
      addToHomeScreen: {
        verticalOffset:
          storeScreenTheme?.data?.addToHomeScreen?.verticalOffset ||
          defaultStoreTheme.addToHomeScreen.verticalOffset,
        active:
          storeScreenTheme?.data?.addToHomeScreen?.active ||
          defaultStoreTheme.addToHomeScreen.active,
        buttonImage:
          storeScreenTheme?.data?.addToHomeScreen?.buttonImage ||
          defaultStoreTheme.addToHomeScreen.buttonImage,
        iconImage:
          storeScreenTheme?.data?.addToHomeScreen?.iconImage ||
          defaultStoreTheme.addToHomeScreen.iconImage,
        shortcutName:
          storeScreenTheme?.data?.addToHomeScreen?.shortcutName ||
          defaultStoreTheme.addToHomeScreen.shortcutName,
        borderColor: {
          colorOne:
            storeScreenTheme?.data?.addToHomeScreen?.borderColor?.colorOne ||
            defaultStoreTheme.addToHomeScreen.borderColor.colorOne
        },
        backgroundColor: {
          colorOne:
            storeScreenTheme?.data?.addToHomeScreen?.backgroundColor
              ?.colorOne ||
            defaultStoreTheme.addToHomeScreen.backgroundColor.colorOne,
          colorTwo:
            storeScreenTheme?.data?.addToHomeScreen?.backgroundColor
              ?.colorTwo ||
            defaultStoreTheme.addToHomeScreen.backgroundColor.colorTwo,
          gradientDirection:
            storeScreenTheme?.data?.addToHomeScreen?.backgroundColor
              ?.gradientDirection ||
            defaultStoreTheme.addToHomeScreen.backgroundColor.gradientDirection
        }
      },
      noOffersTitleText:
        storeScreenTheme?.data?.noOffersTitleText ||
        defaultStoreTheme.noOffersTitleText,
      noOffersMessageText:
        storeScreenTheme?.data?.noOffersMessageText ||
        defaultStoreTheme.noOffersMessageText,
      bundleBorderColor: {
        colorOne:
          storeScreenTheme?.data?.bundleBorderColor?.colorOne ||
          defaultStoreTheme.bundleBorderColor?.colorOne,
        colorTwo:
          storeScreenTheme?.data?.bundleBorderColor?.colorTwo ||
          defaultStoreTheme.bundleBorderColor?.colorTwo,
        gradientDirection:
          storeScreenTheme?.data?.bundleBorderColor?.gradientDirection ||
          defaultStoreTheme.bundleBorderColor?.gradientDirection
      },
      defaultPlayerImage:
        storeScreenTheme?.data?.defaultPlayerImage ??
        defaultStoreTheme.defaultPlayerImage,
      richText: {
        text: storeScreenTheme?.data?.richText?.text
          ? Base64.decode(storeScreenTheme.data.richText.text)
          : defaultStoreTheme.richText?.text ?? '',
        fontColor:
          storeScreenTheme?.data?.richText?.fontColor ??
          defaultStoreTheme.richText?.fontColor ??
          '#000000'
      }
    });
  }, [storeScreenTheme.data, getSections?.data, getTabs?.data, location]);

  return { initialValues: safeInitialValues };
};
